<template>
  <div v-if="show">
    <button
      type="button"
      aria-label="Close"
      class="close p-3"
      @click.prevent="close"
    >
        <span class="material-icons">
          keyboard_arrow_up
        </span>
    </button>
    <b-tabs card v-model="tabIndex">
      <b-tab title="Time off adjustments history" active>
        <div v-if="show === 'form'" style="max-width: 500px;">
          <b-card no-body class="mb-3 border-0">
            <FormGenerator
              ref="form"
              :data="form.fields"
              :textButtonSubmit="submitButtonText"
              textButtonReset="Cancel"
              @submit="submit"
              @reset="switchToHistory"
              @updateItem="updateItem"
            >
              <template v-slot:allowed="slotProps">
                <b-form-group>
                  <label class="form__label">{{ slotProps.data.label }}</label>
                  <b-form-input
                    type="text"
                    :placeholder="slotProps.data.placeholder"
                    v-model="slotProps.data.value"
                    readonly
                  />
                  <slot name="errors"></slot>
                </b-form-group>
              </template>
              <template v-slot:description="slotProps">
                <b-form-group>
                  <label class="form__label">{{ slotProps.data.label }}</label>
                  <b-form-textarea
                    id="textarea"
                    v-model="slotProps.data.value"
                    rows="2"
                    max-rows="6"
                  />
                </b-form-group>
              </template>
            </FormGenerator>
            <div class="text-left text-muted" style="margin-top: -25px;">
              <font-awesome-icon icon="info" style="font-size: 0.75rem;"/>
              <template v-if="isPayable">
                This is payable time off
              </template>
              <template v-else>
                This is not payable time off
              </template>
            </div>
          </b-card>
        </div>
        <div v-else class="text-right">
          <b-button
            variant="success"
            @click.prevent="show = 'form'"
          >Add adjustment</b-button>
        </div>
        <div v-if="history.adjustments" class="text-left">
          <div v-for="(timeOffSettingItems, timeOffSettingName) in history.adjustments" :key="timeOffSettingName" class="mt-3">
            <h6>
              <TimeOffIcon :name="form.fields.time_offs_setting.value.icon" variant="secondary" />
              {{ timeOffSettingName }}
            </h6>
            <div v-for="(adjustItem, adjustIndex) in timeOffSettingItems" :key="adjustIndex">
              <div>
                <span class="font-weight-bold">{{ adjustItem.manager.name }}</span>
                updated at {{ _toLocalTime(adjustItem.updated_at) }}
              </div>
              <span v-if="adjustItem.adjust > 0" style="color:green">+{{ adjustItem.adjust }}</span>
              <span v-if="adjustItem.adjust < 0" style="color:red">{{ adjustItem.adjust }}</span>
              with description "<span class="font-italic">{{ adjustItem.description }}</span>"
            </div>
          </div>
        </div>
        <div v-else>No adjustments yet</div>
      </b-tab>
      <b-tab title="Accrue history" class="text-left">
        <div v-if="history.accruals" class="text-left">
          <div v-for="(timeOffSettingItems, timeOffSettingName) in history.accruals" :key="timeOffSettingName" class="mt-3">
            <h6>
              <TimeOffIcon :name="form.fields.time_offs_setting.value.icon" variant="secondary" />
              {{ timeOffSettingName }}
            </h6>
            <div v-for="(accrualItem, accrualIndex) in timeOffSettingItems" :key="accrualIndex">
              Accrued +{{ accrualItem.accrue }} time off{{ accrualItem.accrue > 1 ? 's' : '' }}
              at {{ _toLocalTime(accrualItem.created_at) }} {{ accrualItem.description }}
            </div>
          </div>
        </div>
        <div v-else>No accruals yet</div>
      </b-tab>
    </b-tabs>

  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import FormGenerator from '@components/FormGenerator'
import TimeOffsSettings from '../../mixins/TimeOffsSettings'
import TimeOffIcon from '@components/TimeOffIcon.vue'

export default {
  name: 'TimeOffAdjustments',
  mixins: [ TimeOffsSettings ],
  components: {
    FormGenerator,
    TimeOffIcon
  },
  props: {
    value: {
      type: String,
      default: null
    },
    userId: {
      type: Number
    },
    year: {
      type: Number
    },
    timeOffsData: {
      type: Object
    }
  },
  data () {
    const defaultForm = {
      fields: {
        'time_offs_setting': {
          type: 'select',
          name: 'time_offs_settings_id',
          label: 'Time off type',
          selectLabel: 'name',
          valueField: 'id',
          options: [],
          value: null,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12 col-md-6'
        },
        'allowed': {
          type: 'slot',
          slotName: 'allowed',
          label: 'Allowed',
          value: '',
          disabled: true,
          class: 'col-12 col-md-6'
        },
        'adjustment_type': {
          type: 'select',
          name: 'adjustment_type',
          label: 'Adjustment type',
          selectLabel: 'label',
          valueField: 'sign',
          options: [{ sign: '+', label: 'Accrue (+)' }, { sign: '-', label: 'Subtract (-)' }],
          value: { sign: '+', label: 'Accrue (+)' },
          validation: [
            { name: 'required' }
          ],
          class: 'col-12 col-md-6'
        },
        'adjust': {
          type: 'input',
          name: 'adjust',
          label: 'Adjust',
          value: 1,
          validation: [
            { name: 'required' }
          ],
          class: 'col-12 col-md-6'
        },
        'description': {
          type: 'slot',
          slotName: 'description',
          name: 'description',
          label: 'Description',
          placeholder: 'Enter description',
          value: null,
          class: 'col-12 col-md-12'
        }
      }
    }

    return {
      defaultForm,
      form: cloneDeep(defaultForm),
      history: {},
      submitButtonText: 'Save'
    }
  },
  watch: {
    'form.fields.time_offs_setting.value': {
      deep: true,
      handler (timeOffsSettingValue) {
        const timeOffsDataByName = Object.values(this.timeOffsData).find(item => item.time_offs_name === timeOffsSettingValue.name)
        this.form.fields.allowed.value = timeOffsDataByName.time_offs_allowed
      }
    },
    'timeOffsData': {
      deep: true,
      handler (timeOffsData) {
        this.tabIndex = this.value === 'accrue-history' ? 1 : 0
        const timeOffsDataByName = Object.values(this.timeOffsData).find(item => item.time_offs_name === this.form.fields.time_offs_setting.value.name)
        this.form.fields.allowed.value = timeOffsDataByName.time_offs_allowed
      }
    }
  },
  computed: {
    ...mapGetters({
      getIsModalLoading: 'finances/getIsModalLoading',
      timeOffsSettings: 'timeOffsSettings/getTimeOffsSettings'
    }),
    isPayable () {
      return this.form.fields.time_offs_setting.value && this.form.fields.time_offs_setting.value.is_payable
    },
    show: {
      get: function () {
        return this.value
      },
      set: function (value) {
        this.$emit('input', value)
      }
    },
    tabIndex: {
      get: function () {
        return this.value === 'accrue-history' ? 1 : 0
      },
      set: function (value) {
        this.$emit('input', 'accrue-history')
      }
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    updateItem (key, itemData) {
      this.form.fields[key] = itemData
    },
    init () {
      this.form.fields.time_offs_setting.options = this.timeOffsSettings
      this.form.fields.time_offs_setting.value = this.timeOffsSettings[0]
      this.fetchAdustments(this.userId, this.year)
    },
    async submit (fields) {
      const data = this.prepareFormFieldsForRequest(fields)
      data['user_id'] = this.userId
      data['year'] = this.year
      this.$api.timeOffsAdjustments.addTimeOffsAdjustment(data)
        .then(async () => {
          this.$snotify.success(
            'Time off successfully adjusted',
            'Success',
            this.$store.state.snotify
          )
          this.init()
          this.switchToHistory()
          this.$emit('dataUpdated')
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
    },
    switchToHistory () {
      this.show = 'history'
    },
    close () {
      this.show = null
    },
    async fetchAdustments (userId, year) {
      const response = await this.$api.timeOffsAdjustments.fetchTimeOffsAdjustments(userId, year)
      this.history = response.data
    },
    prepareFormFieldsForRequest (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const name = field['name']
        const value = field['value']
        if (!value) {
          continue
        }
        data[name] = typeof value === 'object' && !(value instanceof Array) ? value[field['valueField']] : value
      }
      return data
    }
  }
}
</script>
